import { useState, type CSSProperties, type ReactNode } from "react";
import { Eraser } from "lucide-react";
import type { ResolvedTheme } from "@baze/catalog";
import { TEXT_EFFECTS, type SectionStyle, type TextEffect } from "@baze/schema";
import { fontStack, readableOn, splitEmphasis } from "@baze/engine";
import { t } from "@/i18n/id";
import { cn } from "@/lib/cn";
import { ColorField, type Swatch } from "./ColorField";

type Emphasis = NonNullable<SectionStyle["emphasis"]>;

interface Token {
  word: string;
  start: number;
  end: number;
}

const tokenize = (title: string): Token[] => [...title.matchAll(/\S+/g)].map((m) => ({ word: m[0], start: m.index, end: m.index + m[0].length }));

// Punctuation at the edges stays unmarked ("sehat," marks "sehat").
const trimPunct = (s: string, start: number) => {
  const lead = s.match(/^["'“‘(«[]+/)?.[0].length ?? 0;
  const trail = s.match(/[.,!?;:"'”’)»\]…]+$/)?.[0].length ?? 0;
  const text = s.slice(lead, s.length - trail);
  return text ? { text, start: start + lead } : { text: s, start };
};

/** The [first, last] token range the stored emphasis covers, or null when its words are gone. */
function rangeOf(title: string, tokens: Token[], em: Emphasis | undefined): [number, number] | null {
  if (!em) return null;
  const parts = splitEmphasis(title, em.text, em.nth);
  if (!parts) return null;
  const from = parts[0].length;
  const to = from + em.text.length;
  const a = tokens.findIndex((tk) => tk.end > from);
  const b = tokens.findLastIndex((tk) => tk.start < to);
  return a >= 0 && b >= a ? [a, b] : null;
}

/** Phrase + occurrence for a token range, ready to store. */
function phraseFor(title: string, tokens: Token[], a: number, b: number): Pick<Emphasis, "text" | "nth"> {
  const { text, start } = trimPunct(title.slice(tokens[a]!.start, tokens[b]!.end), tokens[a]!.start);
  let nth = 0;
  for (let at = title.indexOf(text); at >= 0 && at <= start; at = title.indexOf(text, at + 1)) nth++;
  return nth > 1 ? { text, nth } : { text };
}

const CIRCLE_MASK =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 60' preserveAspectRatio='none'%3E%3Cpath d='M112 7C55 3 9 15 7 31c-2 17 52 25 107 23 52-2 82-14 80-30C192 9 150 3 86 9' fill='none' stroke='%23000' stroke-width='3.2' stroke-linecap='round' vector-effect='non-scaling-stroke'/%3E%3C/svg%3E\") center / 100% 100% no-repeat";

/** Editor-side copy of the engine's .baze-em rules (the panel is outside the site's stylesheet scope). */
function effectStyle(effect: TextEffect, c: string, primary: string): CSSProperties {
  const clone: CSSProperties = { WebkitBoxDecorationBreak: "clone", boxDecorationBreak: "clone" };
  switch (effect) {
    case "highlight":
      return { ...clone, padding: "0 0.1em", margin: "0 -0.1em", borderRadius: "0.15em 0.4em 0.2em 0.35em", background: `linear-gradient(100deg, ${c}4d, ${c}9e 8% 92%, ${c}66) no-repeat 0 82% / 100% 0.5em` };
    case "underline":
      return { ...clone, paddingBottom: "0.04em", background: `linear-gradient(${c}, ${c}) no-repeat 0 100% / 100% 0.11em` };
    case "wavy":
      return { textDecoration: `underline wavy ${c}`, textDecorationThickness: "0.055em", textUnderlineOffset: "0.2em", textDecorationSkipInk: "none" };
    case "circle":
      return { position: "relative", whiteSpace: "nowrap" };
    case "gradient":
      return { background: `linear-gradient(100deg, ${primary}, ${c})`, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" };
    case "italic":
      return { fontStyle: "italic", color: c };
    case "outline":
      return { color: "transparent", WebkitTextStroke: `max(1px, 0.03em) ${c}` };
    case "box":
      return { ...clone, padding: "0 0.16em", borderRadius: "0.2em", background: c, color: readableOn(c) };
  }
}

function Marked({ effect, color, primary, children }: { effect: TextEffect; color: string; primary: string; children: ReactNode }) {
  return (
    <span style={effectStyle(effect, color, primary)}>
      {children}
      {effect === "circle" ? <span aria-hidden="true" className="pointer-events-none absolute" style={{ inset: "-0.22em -0.32em -0.18em", background: color, WebkitMask: CIRCLE_MASK, mask: CIRCLE_MASK }} /> : null}
    </span>
  );
}

/**
 * "Efek teks judul": the title itself is the picker. Click a word to mark it, click another to stretch
 * the mark to it; the effect shows live in the site's own font and colours, then pick effect and colour.
 */
export function EmphasisPicker({
  title,
  emphasis,
  theme,
  swatches,
  onChange,
}: {
  title: string;
  emphasis: Emphasis | undefined;
  theme: ResolvedTheme;
  swatches: Swatch[];
  onChange: (next: Emphasis | undefined, key?: string) => void;
}) {
  const tokens = tokenize(title);
  const range = rangeOf(title, tokens, emphasis);
  // Where a two-click range started; null when the next click starts a new mark.
  const [anchor, setAnchor] = useState<number | null>(null);
  const c = theme.tokens.colors;
  const effect: TextEffect = emphasis?.effect ?? "highlight";
  const color = emphasis?.color ?? c.accent;
  const lost = !!emphasis && !range;

  const mark = (a: number, b: number, patch?: Partial<Emphasis>) =>
    onChange({ effect, ...(emphasis?.color ? { color: emphasis.color } : {}), ...patch, ...phraseFor(title, tokens, Math.min(a, b), Math.max(a, b)) });

  const clickWord = (i: number) => {
    if (anchor !== null && range) {
      mark(anchor, i);
      setAnchor(null);
      return;
    }
    if (range && range[0] === i && range[1] === i) return clear();
    mark(i, i);
    setAnchor(i);
  };

  const markAll = (last: boolean) => {
    if (!tokens.length) return;
    mark(last ? tokens.length - 1 : 0, tokens.length - 1);
    setAnchor(null);
  };
  const clear = () => {
    onChange(undefined);
    setAnchor(null);
  };
  const setColor = (value: string | undefined, key?: string) => {
    if (!emphasis) return;
    const { color: _old, ...rest } = emphasis;
    onChange(value ? { ...rest, color: value } : rest, key);
  };

  const pickEffect = (e: TextEffect) => {
    if (range) mark(range[0], range[1], { effect: e });
    else if (tokens.length) mark(tokens.length - 1, tokens.length - 1, { effect: e });
  };

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-xl ring-1 ring-line" style={{ background: c.background, color: c.text }}>
        <p className="px-3.5 pt-3 pb-3 text-[19px] leading-[1.35] font-bold" style={{ fontFamily: fontStack(theme.fonts.heading) }}>
          {tokens.map((tk, i) => {
            const inRange = !!range && i >= range[0] && i <= range[1];
            const word = (
              <button
                type="button"
                onClick={() => clickWord(i)}
                aria-pressed={inRange}
                className={cn(
                  "cursor-pointer rounded-[3px] transition outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  !inRange && "hover:bg-current/10",
                  anchor === i && "ring-1 ring-current/30",
                )}
              >
                {tk.word}
              </button>
            );
            const gap = i < tokens.length - 1 ? " " : "";
            // Consecutive marked words share one effect span, so a marker runs through the spaces.
            if (inRange && i === range![0]) {
              const run = tokens.slice(range![0], range![1] + 1);
              return (
                <span key={i}>
                  <Marked effect={effect} color={color} primary={c.primary}>
                    {run.map((r, j) => (
                      <span key={j}>
                        <button
                          type="button"
                          onClick={() => clickWord(range![0] + j)}
                          aria-pressed
                          className={cn("cursor-pointer rounded-[3px] outline-none focus-visible:ring-2 focus-visible:ring-primary", anchor === range![0] + j && "ring-1 ring-current/30")}
                        >
                          {r.word}
                        </button>
                        {j < run.length - 1 ? " " : ""}
                      </span>
                    ))}
                  </Marked>
                  {range![1] < tokens.length - 1 ? " " : ""}
                </span>
              );
            }
            if (inRange) return null;
            return (
              <span key={i}>
                {word}
                {gap}
              </span>
            );
          })}
        </p>
        <div className="flex flex-wrap items-center gap-1.5 border-t px-2.5 py-2" style={{ borderColor: `${c.text}1f`, background: `${c.text}08` }}>
          <p className="mr-auto text-[11.5px] opacity-70">{anchor !== null ? t.editor.style.emphasis.extendHint : t.editor.style.emphasis.hint}</p>
          <QuickButton onClick={() => markAll(true)}>{t.editor.style.emphasis.lastWord}</QuickButton>
          <QuickButton onClick={() => markAll(false)}>{t.editor.style.emphasis.all}</QuickButton>
          {emphasis ? (
            <QuickButton onClick={clear} label={t.editor.style.emphasis.clear}>
              <Eraser className="size-3.5" aria-hidden="true" />
            </QuickButton>
          ) : null}
        </div>
      </div>
      {lost ? <p className="text-[12px] text-amber-700">{t.editor.style.emphasis.lost(emphasis!.text)}</p> : null}

      <div className="grid grid-cols-4 gap-1.5">
        {TEXT_EFFECTS.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => pickEffect(e)}
            aria-pressed={!!emphasis && effect === e}
            className="group flex min-h-[4.25rem] min-w-0 flex-col items-center justify-center gap-1.5 rounded-lg px-1 py-2 outline-none transition hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-primary aria-pressed:bg-primary-soft/70"
          >
            <span className="text-[17px] leading-none font-bold text-slate-900" style={{ fontFamily: fontStack(theme.fonts.heading) }}>
              <Marked effect={e} color={color} primary={c.primary}>
                Aa
              </Marked>
            </span>
            <span className="line-clamp-2 w-full text-center text-[11px] leading-tight font-medium text-slate-500 group-aria-pressed:font-semibold group-aria-pressed:text-slate-900">{t.editor.style.emphasis.effects[e]}</span>
          </button>
        ))}
      </div>

      {emphasis ? (
        <div className="flex items-center gap-1.5">
          {[
            { hex: c.accent, label: t.editor.style.colors.accent, value: undefined },
            { hex: c.primary, label: t.editor.style.colors.primary, value: c.primary },
            { hex: c.secondary, label: t.editor.style.colors.secondary, value: c.secondary },
          ].map((s) => {
            const on = emphasis.color === s.value;
            return (
              <button
                key={s.label}
                type="button"
                title={s.label}
                aria-label={s.label}
                aria-pressed={on}
                onClick={() => setColor(s.value)}
                className="size-8 shrink-0 rounded-full ring-1 ring-slate-900/10 transition ring-inset hover:scale-105 aria-pressed:ring-2 aria-pressed:ring-primary aria-pressed:ring-offset-2"
                style={{ background: s.hex }}
              />
            );
          })}
          <ColorField size="swatch" label={t.editor.style.emphasis.otherColor} value={color} swatches={swatches} onChange={(hex) => setColor(hex, "emphasis-color")} />
          <span className="font-mono text-[11px] tracking-wide text-slate-400 uppercase">{color.slice(1)}</span>
        </div>
      ) : null}
    </div>
  );
}

function QuickButton({ onClick, children, label }: { onClick: () => void; children: ReactNode; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="inline-flex h-7 items-center gap-1 rounded-full px-2.5 text-[11.5px] font-semibold ring-1 ring-current/15 transition hover:bg-current/10"
    >
      {children}
    </button>
  );
}
